import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronLeft, ChevronRight, ArrowRight, X as XIcon } from 'lucide-react';

/**
 * DateRangePicker
 * Props:
 * - startDate: Date | null
 * - endDate: Date | null
 * - onChange: (since: Date | null, until: Date | null) => void
 * - className?: string (additional classes for the trigger button)
 *
 * Behavior:
 * - Maintains an internal selection while the popover is open.
 * - On Apply, calls onChange with the selected range.
 * - Includes a Clear action to reset to null/null.
 * - Popover opens to the LEFT of the trigger, aligned vertically.
 */
const DateRangePicker = ({ startDate = null, endDate = null, onChange, className = '' }) => {
  const today = new Date(new Date().setHours(0, 0, 0, 0));
  const yesterday = new Date(
    new Date(new Date().setDate(today.getDate() - 1)).setHours(0, 0, 0, 0)
  );
  
  const ranges = [
    {
      title: "Today",
      alias: "today",
      period: { since: today, until: today },
    },
    {
      title: "Yesterday",
      alias: "yesterday",
      period: { since: yesterday, until: yesterday },
    },
    {
      title: "Last 7 days",
      alias: "last7days",
      period: {
        since: new Date(new Date(new Date().setDate(today.getDate() - 7)).setHours(0, 0, 0, 0)),
        until: yesterday,
      },
    },
    {
      title: "Last 30 days",
      alias: "last30days",
      period: {
        since: new Date(new Date(new Date().setDate(today.getDate() - 30)).setHours(0, 0, 0, 0)),
        until: yesterday,
      },
    },
    {
      title: "Last 90 days",
      alias: "last90days",
      period: {
        since: new Date(new Date(new Date().setDate(today.getDate() - 90)).setHours(0, 0, 0, 0)),
        until: yesterday,
      },
    },
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [activeDateRange, setActiveDateRange] = useState(null);
  const [inputValues, setInputValues] = useState({ since: '', until: '' });
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedStart, setSelectedStart] = useState(startDate);
  const [selectedEnd, setSelectedEnd] = useState(endDate);
  const [hoveredDate, setHoveredDate] = useState(null);

  const containerRef = useRef(null);

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDateDisplay = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentYear, currentMonth, day);
    if (!selectedStart || (selectedStart && selectedEnd)) {
      setSelectedStart(clickedDate);
      setSelectedEnd(null);
      setActiveDateRange(null);
    } else if (selectedStart && !selectedEnd) {
      if (clickedDate >= selectedStart) {
        setSelectedEnd(clickedDate);
        setActiveDateRange({
          alias: "custom",
          title: "Custom",
          period: { since: selectedStart, until: clickedDate },
        });
      } else {
        setSelectedStart(clickedDate);
        setSelectedEnd(null);
        setActiveDateRange(null);
      }
    }
  };

  const handleRangeSelect = (range) => {
    setActiveDateRange(range);
    setSelectedStart(range.period.since);
    setSelectedEnd(range.period.until);
    setCurrentMonth(range.period.since.getMonth());
    setCurrentYear(range.period.since.getFullYear());
  };

  const handleApply = () => {
    setIsOpen(false);
    if (typeof onChange === 'function') {
      onChange(selectedStart || null, selectedEnd || null);
    }
  };

  const handleClear = () => {
    setSelectedStart(null);
    setSelectedEnd(null);
    setActiveDateRange({ alias: 'custom', title: 'Custom', period: { since: today, until: today } });
    setInputValues({ since: '', until: '' });
    if (typeof onChange === 'function') {
      onChange(null, null);
    }
    setIsOpen(false);
  };

  const isDateInRange = (day) => {
    if (!selectedStart) return false;
    const date = new Date(currentYear, currentMonth, day);
    
    if (selectedEnd) {
      return date >= selectedStart && date <= selectedEnd;
    } else if (hoveredDate && hoveredDate >= selectedStart) {
      return date >= selectedStart && date <= hoveredDate;
    }
    
    return date.getTime() === selectedStart.getTime();
  };

  const isDateStart = (day) => {
    if (!selectedStart) return false;
    const date = new Date(currentYear, currentMonth, day);
    return date.getTime() === selectedStart.getTime();
  };

  const isDateEnd = (day) => {
    if (!selectedEnd) return false;
    const date = new Date(currentYear, currentMonth, day);
    return date.getTime() === selectedEnd.getTime();
  };

  const isToday = (day) => {
    const date = new Date(currentYear, currentMonth, day);
    return date.toDateString() === today.toDateString();
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-8 h-8"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isInRange = isDateInRange(day);
      const isStart = isDateStart(day);
      const isEnd = isDateEnd(day);
      const isTodayDate = isToday(day);
      
      days.push(
        <button
          key={day}
          className={`
            w-8 h-8 text-sm rounded-md flex items-center justify-center
            ${isStart || isEnd
              ? 'bg-blue-600 text-white'
              : isInRange
              ? 'bg-gray-100 text-gray-600'
              : isTodayDate
              ? 'bg-gray-100 text-gray-700 font-semibold'
              : 'text-gray-600 hover:bg-gray-50'
            }
            transition-colors duration-150
          `}
          onClick={() => handleDateClick(day)}
          onMouseEnter={() => {
            if (selectedStart && !selectedEnd) {
              setHoveredDate(new Date(currentYear, currentMonth, day));
            }
          }}
          onMouseLeave={() => setHoveredDate(null)}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  // Determine button label: prefer controlled props, else use active preset
  const buttonValue = (() => {
    if (startDate && endDate) {
      return `${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`;
    }
    if (startDate && !endDate) {
      return `${formatDateDisplay(startDate)} - …`;
    }
    if (!startDate && !endDate && activeDateRange) {
      return activeDateRange.title;
    }
    return 'Select dates';
  })();

  // Keep input fields in sync with either the active preset or the current custom selection
  useEffect(() => {
    if (activeDateRange && activeDateRange.period) {
      setInputValues({
        since: formatDateDisplay(activeDateRange.period.since),
        until: formatDateDisplay(activeDateRange.period.until),
      });
      setSelectedStart(activeDateRange.period.since);
      setSelectedEnd(activeDateRange.period.until);
    } else if (selectedStart && selectedEnd) {
      setInputValues({
        since: formatDateDisplay(selectedStart),
        until: formatDateDisplay(selectedEnd),
      });
    } else if (selectedStart && !selectedEnd) {
      setInputValues({
        since: formatDateDisplay(selectedStart),
        until: '',
      });
    } else {
      setInputValues({ since: '', until: '' });
    }
  }, [activeDateRange, selectedStart, selectedEnd]);

  // Sync internal state if parent-controlled values change
  useEffect(() => {
    setSelectedStart(startDate || null);
  }, [startDate]);

  useEffect(() => {
    setSelectedEnd(endDate || null);
  }, [endDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center h-12 px-6 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500/50 focus:ring-offset-0 min-w-[100px] ${className}`}
      >
        <Calendar className="w-4 h-4 mr-2 text-gray-600" />
        {buttonValue}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200  p-0 min-w-96" >
          <div className="flex">
            {/* Left sidebar with ranges */}
            <div className="w-40 bg-gray-50 p-3 rounded-l-lg border-r">
              <div className="space-y-1">
        {ranges.map((range) => (
                  <button
                    key={range.alias}
                    onClick={() => handleRangeSelect(range)}
                    className={`
                      w-full text-left px-2 py-2 text-sm rounded-md transition-colors
          ${activeDateRange?.alias === range.alias
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                  >
                    {range.title}
                  </button>
                ))}
              </div>
            </div>

            {/* Right side with inputs and calendar */}
            <div className="flex-1 p-4">
              {/* Date inputs */}
              <div className="flex items-center gap-2 mb-4">
                <div className="flex-1">
                  <input
                    type="date"
                    value={inputValues.since || ''}
                    onChange={(e) => {
                      setInputValues(prev => ({ ...prev, since: e.target.value }));
                      if (e.target.value) {
                        const date = parseDate(e.target.value);
                        setSelectedStart(date);
                        if (selectedEnd && date > selectedEnd) {
                          setSelectedEnd(null);
                        }
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-600"
                  />
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <div className="flex-1">
                  <input
                    type="date"
                    value={inputValues.until || ''}
                    onChange={(e) => {
                      setInputValues(prev => ({ ...prev, until: e.target.value }));
                      if (e.target.value) {
                        const date = parseDate(e.target.value);
                        setSelectedEnd(date);
                        if (selectedStart && date < selectedStart) {
                          setSelectedStart(date);
                          setSelectedEnd(selectedStart);
                        }
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
              </div>

              {/* Calendar */}
              <div className="bg-white">
                {/* Calendar header */}
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => navigateMonth('prev')}
                    className="p-2 hover:bg-gray-100 rounded-md"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <h3 className="text-sm font-semibold text-gray-600">
                    {monthNames[currentMonth]} {currentYear}
                  </h3>
                  <button
                    onClick={() => navigateMonth('next')}
                    className="p-2 hover:bg-gray-100 rounded-md"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Weekday headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {weekDays.map((day) => (
                    <div
                      key={day}
                      className="w-8 h-8 text-xs font-medium text-gray-500 flex items-center justify-center"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {renderCalendarDays()}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex justify-between items-center gap-2 mt-4 pt-4 border-t">
                <button
                  onClick={handleClear}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  title="Clear dates"
                >
                  <XIcon className="w-4 h-4" /> Clear
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApply}
                    className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;